// useLpDealerData — Central orchestration hook for the LP Treasury Dealer dashboard
// Replaces useTreasuryDealerData.js with full adapter-driven state management.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { MockVenueAdapter } from '../services/lp/MockVenueAdapter';
import { MockBankPricingAdapter } from '../services/lp/MockBankPricingAdapter';
import { MockBankTreasuryAdapter } from '../services/lp/MockBankTreasuryAdapter';
import { mockTrades, mockAutoRules, mockDealerLimits } from '../data/lpMockData';
import { STALE_FEED_THRESHOLD_MS, DEADLINE_WARNING_MS, ALERT_SEVERITIES, ALERT_TYPES } from '../lib/lp/constants';
import { isActionable } from '../lib/lp/quoteStateMachine';
import { isLateFunds } from '../lib/lp/tradeStateMachine';
import { deriveClientRate, computeCounterAmount, evaluatePreTradeChecks, checkMarginBand } from '../lib/lp/pricing';

let _alertIdCounter = 1;

const useLpDealerData = () => {
  const { user } = useAuth();
  const servicesRef = useRef(null);
  const unsubsRef = useRef([]);

  // ─── Core State ─────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rfqs, setRfqs] = useState({});
  const [quotes, setQuotes] = useState({});
  const [trades, setTrades] = useState({});
  const [counterparties, setCounterparties] = useState({});
  const [priceFeed, setPriceFeed] = useState({});
  const [cbReference, setCbReference] = useState(null);
  const [bookContext, setBookContext] = useState(null);
  const [competitiveContext, setCompetitiveContext] = useState(null);
  const [autoRules, setAutoRules] = useState(mockAutoRules);
  const [dealerLimits] = useState(mockDealerLimits);
  const [alerts, setAlerts] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  // ─── Derived Values ─────────────────────────────────────────────────
  const rfqList = useMemo(() => Object.values(rfqs).sort((a, b) =>
    new Date(a.deadline) - new Date(b.deadline)
  ), [rfqs]);

  const quoteList = useMemo(() => Object.values(quotes).sort((a, b) =>
    new Date(b.submittedAt) - new Date(a.submittedAt)
  ), [quotes]);

  const tradeList = useMemo(() => Object.values(trades).sort((a, b) =>
    new Date(b.bookedAt) - new Date(a.bookedAt)
  ), [trades]);

  const liveRfqs = useMemo(() => rfqList.filter(r =>
    r.status === 'live' || r.status === 'quoted'
  ), [rfqList]);

  const activeQuotes = useMemo(() => quoteList.filter(q =>
    isActionable(q.status)
  ), [quoteList]);

  // ─── Alert Helper ───────────────────────────────────────────────────
  const addAlert = useCallback((type, severity, message, entityId) => {
    setAlerts(prev => [{
      id: `ALT-${_alertIdCounter++}`,
      type,
      severity,
      message,
      entityId,
      timestamp: new Date().toISOString(),
      dismissed: false,
    }, ...prev]);
  }, []);

  const dismissAlert = useCallback((alertId) => {
    setAlerts(prev => prev.map(a =>
      a.id === alertId ? { ...a, dismissed: true } : a
    ));
  }, []);

  // ─── Initialize Services & Subscriptions ────────────────────────────
  useEffect(() => {
    const venue = new MockVenueAdapter();
    const pricing = new MockBankPricingAdapter();
    const treasury = new MockBankTreasuryAdapter();
    servicesRef.current = { venue, pricing, treasury };

    // Load initial trades
    const tradesMap = {};
    mockTrades.forEach(t => { tradesMap[t.id] = { ...t }; });
    setTrades(tradesMap);

    // Subscribe to RFQ stream
    const unsubRfqs = venue.streamRfqs((rfq) => {
      setRfqs(prev => ({ ...prev, [rfq.id]: rfq }));
    });

    // Subscribe to price stream
    const unsubPrices = pricing.streamPrices((tick) => {
      setPriceFeed(prev => ({
        ...prev,
        [tick.pair]: { bid: tick.bid, ask: tick.ask, mid: tick.mid, timestamp: tick.timestamp, latencyMs: tick.latencyMs },
      }));
    });

    // Subscribe to counterparty status changes
    const unsubCpStatus = venue.streamCounterpartyStatus(({ counterpartyId, newStatus }) => {
      setCounterparties(prev => ({
        ...prev,
        [counterpartyId]: prev[counterpartyId]
          ? { ...prev[counterpartyId], kycStatus: newStatus }
          : { id: counterpartyId, kycStatus: newStatus },
      }));

      // Auto-withdraw live quotes for suspended counterparty
      if (newStatus === 'suspended') {
        addAlert(ALERT_TYPES.KYC_STATUS_CHANGE, ALERT_SEVERITIES.CRITICAL,
          `Counterparty ${counterpartyId} suspended — live quotes auto-withdrawn`, counterpartyId);

        setQuotes(prev => {
          const updated = { ...prev };
          for (const [id, q] of Object.entries(updated)) {
            if (q.counterpartyId === counterpartyId && isActionable(q.status)) {
              updated[id] = { ...q, status: 'withdrawn' };
              venue.withdrawQuote(id, 'System').catch(() => {});
            }
          }
          return updated;
        });
      }
    });

    // Load book context and CB reference
    pricing.getBookContext().then(setBookContext);
    pricing.getCbReference().then(setCbReference);

    // Load initial quotes
    const initialQuotes = venue.getQuotes();
    const quotesMap = {};
    initialQuotes.forEach(q => { quotesMap[q.id] = q; });
    setQuotes(quotesMap);

    // Load audit log
    setAuditLog(venue.getAuditLog());

    unsubsRef.current = [unsubRfqs, unsubPrices, unsubCpStatus];
    setLoading(false);

    return () => {
      unsubsRef.current.forEach(unsub => unsub());
      venue.destroy();
      pricing.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Deadline & Stale Feed Alert Timer ──────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      // Check for approaching deadlines
      Object.values(rfqs).forEach(rfq => {
        if (rfq.status !== 'live' && rfq.status !== 'quoted') return;
        const timeLeft = new Date(rfq.deadline).getTime() - now;
        if (timeLeft > 0 && timeLeft < DEADLINE_WARNING_MS) {
          setAlerts(prev => {
            if (prev.some(a => a.entityId === rfq.id && a.type === ALERT_TYPES.DEADLINE_APPROACHING && !a.dismissed)) return prev;
            return [{
              id: `ALT-${_alertIdCounter++}`,
              type: ALERT_TYPES.DEADLINE_APPROACHING,
              severity: ALERT_SEVERITIES.WARNING,
              message: `RFQ ${rfq.id} deadline in ${Math.round(timeLeft / 1000)}s`,
              entityId: rfq.id,
              timestamp: new Date().toISOString(),
              dismissed: false,
            }, ...prev];
          });
        }
      });

      // Check for stale feeds
      Object.entries(priceFeed).forEach(([pair, data]) => {
        if (now - data.timestamp > STALE_FEED_THRESHOLD_MS) {
          setAlerts(prev => {
            if (prev.some(a => a.entityId === pair && a.type === ALERT_TYPES.STALE_FEED && !a.dismissed)) return prev;
            return [{
              id: `ALT-${_alertIdCounter++}`,
              type: ALERT_TYPES.STALE_FEED,
              severity: ALERT_SEVERITIES.WARNING,
              message: `Price feed stale for ${pair}`,
              entityId: pair,
              timestamp: new Date().toISOString(),
              dismissed: false,
            }, ...prev];
          });
        }
      });

      // Check for late funds
      Object.values(trades).forEach(trade => {
        if (isLateFunds(trade)) {
          setAlerts(prev => {
            if (prev.some(a => a.entityId === trade.id && a.type === ALERT_TYPES.LATE_FUNDS && !a.dismissed)) return prev;
            return [{
              id: `ALT-${_alertIdCounter++}`,
              type: ALERT_TYPES.LATE_FUNDS,
              severity: ALERT_SEVERITIES.CRITICAL,
              message: `Late funds: ${trade.currencyPair} ${formatAmount(trade.amount)} (${trade.id})`,
              entityId: trade.id,
              timestamp: new Date().toISOString(),
              dismissed: false,
            }, ...prev];
          });
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [rfqs, priceFeed, trades]);

  // ─── Actions ────────────────────────────────────────────────────────

  const submitQuote = useCallback(async ({ rfqId, margin, side }) => {
    const { venue } = servicesRef.current;
    const rfq = rfqs[rfqId];
    if (!rfq) throw new Error('RFQ not found');

    const feed = priceFeed[rfq.currencyPair];
    const mid = feed?.mid || 0;
    const clientRate = deriveClientRate(mid, margin, side || rfq.side);
    const counterAmt = computeCounterAmount(rfq.amount, clientRate);
    const marginPct = mid > 0 ? Math.abs(margin / mid) : 0;
    const bandCheck = checkMarginBand(rfq.amount, marginPct);

    const quote = await venue.submitQuote({
      rfqId,
      currencyPair: rfq.currencyPair,
      side: side || rfq.side,
      amount: rfq.amount,
      midRate: mid,
      margin,
      clientRate,
      counterAmount: counterAmt,
      dealerName: user?.name || 'Dealer',
      offPolicy: !bandCheck.inBand,
      seniorApproval: null,
    });

    setQuotes(prev => ({ ...prev, [quote.id]: quote }));
    setRfqs(prev => ({
      ...prev,
      [rfqId]: { ...prev[rfqId], status: 'quoted' },
    }));
    refreshAuditLog();
    return quote;
  }, [rfqs, priceFeed, user]);

  const amendQuote = useCallback(async (quoteId, { margin }) => {
    const { venue } = servicesRef.current;
    const quote = quotes[quoteId];
    if (!quote) throw new Error('Quote not found');

    const feed = priceFeed[quote.currencyPair];
    const mid = feed?.mid || quote.midRate;
    const clientRate = deriveClientRate(mid, margin, quote.side);
    const counterAmt = computeCounterAmount(quote.amount, clientRate);

    const updated = await venue.amendQuote(quoteId, {
      margin,
      clientRate,
      counterAmount: counterAmt,
      dealerName: user?.name || 'Dealer',
    });

    setQuotes(prev => ({ ...prev, [quoteId]: updated }));
    refreshAuditLog();
    return updated;
  }, [quotes, priceFeed, user]);

  const withdrawQuote = useCallback(async (quoteId) => {
    const { venue } = servicesRef.current;
    const updated = await venue.withdrawQuote(quoteId, user?.name || 'Dealer');
    setQuotes(prev => ({ ...prev, [quoteId]: updated }));
    refreshAuditLog();
    return updated;
  }, [user]);

  const declineRfq = useCallback(async (rfqId, reason) => {
    const { venue } = servicesRef.current;
    const updated = await venue.declineRfq(rfqId, reason, user?.name || 'Dealer');
    setRfqs(prev => ({ ...prev, [rfqId]: updated }));
    refreshAuditLog();
    return updated;
  }, [user]);

  const confirmTrade = useCallback(async (tradeId) => {
    const { venue } = servicesRef.current;
    await venue.confirmTrade(tradeId);
    setTrades(prev => ({
      ...prev,
      [tradeId]: { ...prev[tradeId], status: 'confirmed', confirmation: { ...prev[tradeId]?.confirmation, bankConfirmed: true } },
    }));
    refreshAuditLog();
  }, []);

  const updateSettlement = useCallback(async (tradeId, newStatus) => {
    const { venue } = servicesRef.current;
    await venue.updateSettlement(tradeId, { status: newStatus });
    setTrades(prev => ({
      ...prev,
      [tradeId]: { ...prev[tradeId], status: newStatus },
    }));
    refreshAuditLog();
  }, []);

  const releaseTrade = useCallback(async (tradeId) => {
    // Role gate: only lp_operations can release
    await updateSettlement(tradeId, 'released');
  }, [updateSettlement]);

  const getCounterparty = useCallback(async (counterpartyId) => {
    const { venue } = servicesRef.current;
    const cp = await venue.getCounterparty(counterpartyId);
    if (cp) {
      setCounterparties(prev => ({ ...prev, [counterpartyId]: cp }));
    }
    return cp;
  }, []);

  const getEvidencePack = useCallback(async (rfqId) => {
    const { venue } = servicesRef.current;
    const pack = await venue.getEvidencePack(rfqId, user?.name || 'Dealer');
    refreshAuditLog();
    return pack;
  }, [user]);

  const exportRegulatory = useCallback(async (filters) => {
    const { treasury } = servicesRef.current;
    return treasury.exportRegulatory(filters);
  }, []);

  const getPreTradeChecks = useCallback((rfqId) => {
    const rfq = rfqs[rfqId];
    if (!rfq) return [];
    const cp = counterparties[rfq.counterpartyId];

    // Determine required docs with labels
    const LP_DOC_TYPE_LABELS = {
      allocation_notice: 'Allocation Notice',
      source_of_funds: 'Source of Funds Declaration',
      purpose_code: 'Purpose Code Certificate',
      cb_form: 'Central Bank Form',
    };
    const requiredDocs = (rfq.requiredDocs || []).map(docType => ({
      type: docType,
      label: LP_DOC_TYPE_LABELS[docType] || docType,
      required: true,
    }));

    return evaluatePreTradeChecks({
      rfq,
      counterparty: cp,
      bookContext,
      dealerLimits,
      priceFeed,
      requiredDocs,
    });
  }, [rfqs, counterparties, bookContext, dealerLimits, priceFeed]);

  const getCompetitiveCtx = useCallback(async (amount) => {
    const { venue } = servicesRef.current;
    const ctx = await venue.getCompetitiveContext(amount);
    setCompetitiveContext(ctx);
    return ctx;
  }, []);

  // ─── Audit Log Refresh ──────────────────────────────────────────────
  const refreshAuditLog = useCallback(() => {
    if (servicesRef.current?.venue) {
      setAuditLog(servicesRef.current.venue.getAuditLog());
    }
  }, []);

  // ─── Metrics ────────────────────────────────────────────────────────
  const metrics = useMemo(() => ({
    activeRfqs: liveRfqs.length,
    activeQuotes: activeQuotes.length,
    dealsToday: tradeList.filter(t => {
      const d = new Date(t.bookedAt);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    }).length,
    volumeToday: tradeList.filter(t => {
      const d = new Date(t.bookedAt);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    }).reduce((sum, t) => sum + t.amount, 0),
    nopUtilization: bookContext?.nopUtilization || 0,
    pendingAlerts: alerts.filter(a => !a.dismissed).length,
  }), [liveRfqs, activeQuotes, tradeList, bookContext, alerts]);

  return {
    // State
    loading,
    error,
    rfqs: rfqList,
    rfqMap: rfqs,
    quotes: quoteList,
    quoteMap: quotes,
    trades: tradeList,
    tradeMap: trades,
    counterparties,
    priceFeed,
    cbReference,
    bookContext,
    competitiveContext,
    autoRules,
    setAutoRules,
    dealerLimits,
    alerts,
    auditLog,
    metrics,
    liveRfqs,
    activeQuotes,

    // Actions
    submitQuote,
    amendQuote,
    withdrawQuote,
    declineRfq,
    confirmTrade,
    updateSettlement,
    releaseTrade,
    getCounterparty,
    getEvidencePack,
    exportRegulatory,
    getPreTradeChecks,
    getCompetitiveCtx,
    dismissAlert,
    refreshAuditLog,
  };
};

function formatAmount(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

export default useLpDealerData;
